import AtriumApi, { getApiErrorMessage } from "api";
import Badge from "components/badge";
import Button from "components/button";
import { InputField } from "components/fields";
import { Form, Formik } from "formik";
import { useState } from "react";
import notify from "lib/notify";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "hooks/useAuth";
import * as Yup from "yup";
import SettingsSection, { SettingsFormActions } from "./components/SettingsSection";

const PASSWORD_INITIAL_VALUES = { old: "", new: "", confirm: "" };

const passwordSchema = Yup.object().shape({
  old: Yup.string().required("Current password is required"),
  new: Yup.string()
    .required("New password is required")
    .notOneOf([Yup.ref("old")], "New password must be different from your current password"),
  confirm: Yup.string()
    .required("Please confirm your new password")
    .oneOf([Yup.ref("new")], "Passwords do not match"),
});

const SecurityForm = ({ user: propUser }) => {
  const { setUser } = useAuth();

  // 2FA state
  const [setupStep, setSetupStep] = useState(null); // null | "qr" | "verify"
  const [qrCode, setQrCode] = useState(null);
  const [manualSecret, setManualSecret] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");
  const [disableError, setDisableError] = useState("");
  const [showDisable, setShowDisable] = useState(false);

  const [is2FAEnabled, setIs2FAEnabled] = useState(propUser?.totpEnabled || false);

  const onUpdatePassword = async (values, { resetForm }) => {
    try {
      const resp = await AtriumApi.put("/v1/profile", {
        profile: { oldPassword: values.old, password: values.new },
      });

      if (resp.status === 422) {
        notify.error(resp.data?.message || "Could not update password");
      } else {
        notify.success("Password saved");
        resetForm();
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not update password"));
    }
  };

  const handleSetup2FA = async () => {
    setIsSettingUp(true);
    try {
      const resp = await AtriumApi.post("/v1/2fa/setup");
      if (resp.data?.success) {
        setQrCode(resp.data.qrCode);
        setManualSecret(resp.data.secret);
        setSetupStep("qr");
      } else {
        notify.error(resp.data?.message || "Could not start two-factor setup");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not start two-factor setup"));
    } finally {
      setIsSettingUp(false);
    }
  };

  const cancelSetup = () => {
    setSetupStep(null);
    setQrCode(null);
    setManualSecret(null);
  };

  const handleVerify2FA = async (event) => {
    event.preventDefault();
    if (!verifyCode || verifyCode.length !== 6) {
      setVerifyError("Enter the 6-digit code from your authenticator app");
      return;
    }

    setIsSettingUp(true);
    try {
      const resp = await AtriumApi.post("/v1/2fa/verify", { code: verifyCode });
      if (resp.data?.success) {
        notify.success("Two-factor authentication enabled");
        setSetupStep(null);
        setQrCode(null);
        setManualSecret(null);
        setVerifyCode("");
        setIs2FAEnabled(true);
        if (resp.data?.token) {
          localStorage.setItem("auth", JSON.stringify(resp.data.token));
          setUser(jwtDecode(resp.data.token));
        }
      } else {
        setVerifyError(resp.data?.message || "Invalid code");
      }
    } catch (error) {
      setVerifyError(getApiErrorMessage(error, "Invalid code. Please try again."));
    } finally {
      setIsSettingUp(false);
    }
  };

  const closeDisable = () => {
    setShowDisable(false);
    setDisablePassword("");
    setDisableError("");
  };

  const handleDisable2FA = async (event) => {
    event.preventDefault();
    if (!disablePassword) {
      setDisableError("Password is required");
      return;
    }

    setIsSettingUp(true);
    try {
      const resp = await AtriumApi.post("/v1/2fa/disable", {
        password: disablePassword,
      });
      if (resp.data?.success) {
        notify.success("Two-factor authentication disabled");
        closeDisable();
        setIs2FAEnabled(false);
      } else {
        setDisableError(resp.data?.message || "Could not disable two-factor authentication");
      }
    } catch (error) {
      setDisableError(getApiErrorMessage(error, "Incorrect password"));
    } finally {
      setIsSettingUp(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Formik
        initialValues={PASSWORD_INITIAL_VALUES}
        validationSchema={passwordSchema}
        onSubmit={onUpdatePassword}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
          <SettingsSection
            title="Change Password"
            description="Use a password you don't use for any other account."
          >
            <Form noValidate className="flex flex-col gap-4 md:max-w-md">
              <InputField
                label="Current Password"
                required
                id="old-password"
                name="old"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your current password"
                value={values.old}
                error={errors.old}
                touched={touched.old}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <InputField
                label="New Password"
                required
                id="new-password"
                name="new"
                type="password"
                autoComplete="new-password"
                placeholder="Enter your new password"
                value={values.new}
                error={errors.new}
                touched={touched.new}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <InputField
                label="Confirm New Password"
                required
                id="confirm-password"
                name="confirm"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your new password"
                value={values.confirm}
                error={errors.confirm}
                touched={touched.confirm}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <SettingsFormActions>
                <Button type="submit" loading={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </Button>
              </SettingsFormActions>
            </Form>
          </SettingsSection>
        )}
      </Formik>

      <SettingsSection
        title="Two-Factor Authentication"
        description="Add an extra layer of security with an authenticator app such as Google Authenticator or Authy."
        action={
          <Badge tone={is2FAEnabled ? "success" : "neutral"} dot>
            {is2FAEnabled ? "Enabled" : "Off"}
          </Badge>
        }
      >
        {is2FAEnabled && !showDisable && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-fg">Authenticator App</p>
              <p className="mt-0.5 text-sm text-fg-muted">
                You'll be asked for a code from your app each time you sign in.
              </p>
            </div>
            <Button variant="secondary" onClick={() => setShowDisable(true)}>
              Disable
            </Button>
          </div>
        )}

        {is2FAEnabled && showDisable && (
          <form onSubmit={handleDisable2FA} noValidate className="md:max-w-md">
            <InputField
              label="Password"
              required
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              hint="Enter your password to disable two-factor authentication."
              value={disablePassword}
              error={disableError}
              onChange={(e) => {
                setDisablePassword(e.target.value);
                if (disableError) setDisableError("");
              }}
            />
            <SettingsFormActions>
              <Button variant="secondary" onClick={closeDisable} disabled={isSettingUp}>
                Cancel
              </Button>
              <Button type="submit" variant="danger" loading={isSettingUp}>
                Disable 2FA
              </Button>
            </SettingsFormActions>
          </form>
        )}

        {!is2FAEnabled && !setupStep && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-fg">Authenticator App</p>
              <p className="mt-0.5 text-sm text-fg-muted">
                Use Google Authenticator, Authy or any TOTP-compatible app.
              </p>
            </div>
            <Button onClick={handleSetup2FA} loading={isSettingUp}>
              Set Up
            </Button>
          </div>
        )}

        {!is2FAEnabled && setupStep === "qr" && (
          <div>
            <p className="text-sm text-fg">Scan this QR code with your authenticator app.</p>
            <div className="my-4 flex justify-center">
              <img
                src={qrCode}
                alt="Two-factor authentication QR code"
                className="h-48 w-48 rounded-control border border-line bg-white p-2"
              />
            </div>
            <div className="rounded-control bg-surface-raised p-3">
              <p className="text-xs text-fg-muted">Can't scan? Enter this code manually:</p>
              <p className="mt-1 break-all font-mono text-sm font-bold tracking-widest text-fg">
                {manualSecret}
              </p>
            </div>
            <SettingsFormActions>
              <Button variant="secondary" onClick={cancelSetup}>
                Cancel
              </Button>
              <Button onClick={() => setSetupStep("verify")}>Next</Button>
            </SettingsFormActions>
          </div>
        )}

        {!is2FAEnabled && setupStep === "verify" && (
          <form onSubmit={handleVerify2FA} noValidate className="md:max-w-md">
            <InputField
              label="Verification Code"
              required
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              hint="Enter the 6-digit code from your authenticator app to finish setup."
              value={verifyCode}
              error={verifyError}
              onChange={(e) => {
                setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (verifyError) setVerifyError("");
              }}
            />
            <SettingsFormActions>
              <Button
                variant="secondary"
                disabled={isSettingUp}
                onClick={() => {
                  setSetupStep("qr");
                  setVerifyCode("");
                  setVerifyError("");
                }}
              >
                Back
              </Button>
              <Button type="submit" loading={isSettingUp}>
                Enable 2FA
              </Button>
            </SettingsFormActions>
          </form>
        )}
      </SettingsSection>
    </div>
  );
};

export default SecurityForm;
